import React, { useEffect, useRef, useState } from 'react';
import { Paintable } from 'ff-canvas';
import styles from './Preview2d.module.scss';
import classNames from 'classnames';
import { AspectRatio } from '@gift/ui/components/aspect-ratio/AspectRatio';
import { createEngine2d, Engine2d } from '@gift/common/helpers/createEngine2d';
import { TransformParameters } from '@gift/common/helpers/transformParameters';
import { Size } from '@gift/types/generic/Size';
import { useResize } from '@gift/ui/hooks/useResize';

export const Preview2d = ({
    paintables,
    transformParameters
}: Props) => {

    const floorIfDefined = (size: Size | undefined) => size && ({
        width: Math.floor(size!.width),
        height: Math.floor(size!.height)
    });

    const containerRef = useRef<HTMLDivElement>(null);
    const containerSize = floorIfDefined(useResize(containerRef));

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const paintablesRef = useRef<Paintable[]>([]);
    const [engine2d, setEngine2d] = useState<Engine2d | null>(null);

    // 2D engine creation:

    useEffect(() => {
        if (process.env.REACT_APP_TO_BUILD !== 'website-server') {
            if (containerSize) {
                if (!engine2d?.is(containerSize, transformParameters)) {
                    setEngine2d(createEngine2d(paintablesRef, canvasRef.current!, containerSize, transformParameters));
                }
            }
        }
    }, [
        engine2d, containerSize, transformParameters
    ]);

    // 2D engine paintables rerender:

    useEffect(() => {
        if (process.env.REACT_APP_TO_BUILD !== 'website-server') {
            if (containerSize) {
                if (engine2d?.is(containerSize, transformParameters)) {
                    paintablesRef.current = paintables;
                    engine2d.requestRender();
                }
            }
        }
    }, [
        engine2d, containerSize, paintables, transformParameters
    ]);

    return (
        <div ref={containerRef} className={styles.container}>
            <AspectRatio width={transformParameters.sceneWidth} height={transformParameters.sceneHeight}>
                <canvas ref={canvasRef} className={classNames('focus:border-none focus:outline-none')} />
            </AspectRatio>
        </div>
    );
};

type Props = {
    paintables: Paintable[],
    transformParameters: TransformParameters
};
